import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useHome from "../hooks/useBlog";
import "../style.css";

const formatDate = (dateStr: string) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const shortHash = (id: string) => {
  if (!id) return "0000000";
  return id.replace(/-/g, "").slice(0, 7);
};

/* Skeleton placeholder while loading */
const GitlogSkeleton = () => (
  <>
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="dev-gitlog-item dev-gitlog-skeleton">
        <div className="dev-gitlog-timeline">
          <div className="dev-gitlog-node" style={{ background: "rgba(251,173,22,0.2)", borderColor: "transparent" }} />
          {i < 3 && <div className="dev-gitlog-line" />}
        </div>
        <div className="dev-gitlog-content" style={{ flex: 1 }}>
          <div className="dev-skeleton-line" style={{ width: "40%", height: 12, marginBottom: 10 }} />
          <div className="dev-skeleton-line" style={{ width: "75%", height: 16, marginBottom: 8 }} />
          <div className="dev-skeleton-line" style={{ width: "90%", height: 12 }} />
        </div>
      </div>
    ))}
  </>
);

const BlogSection = () => {
  const { blogs, isLoading } = useHome();
  const navigate = useNavigate();
  const { t } = useTranslation('home');

  const navigateAllBlog = () => {
    navigate("/blog");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // blogs = { data: [...], meta: {...} } từ Strapi
  const items: any[] = blogs?.data?.slice(0, 4) ?? [];
  const hasLoaded = !isLoading;
  const isEmpty = hasLoaded && items.length === 0;

  return (
    <section className="dev-blog-section">
      <div className="dev-blog-inner">
        {/* Header */}
        <div className="dev-blog-header">
          <div>
            <span className="dev-section-comment">{t('blog.label')}</span>
            <h2 className="dev-section-h2" style={{ margin: "8px 0 0" }}>{t('blog.title')}</h2>
          </div>
          <button className="dev-blog-viewall" onClick={navigateAllBlog}>
            {t('blog.view_all')}
          </button>
        </div>

        {/* Git log container */}
        <div className="dev-gitlog-container">
          <div className="dev-gitlog-head">
            <span className="dev-gitlog-branch">
              <span style={{ color: "#50FA7B" }}>●</span>
              &nbsp;origin/news &nbsp;
              <span style={{ color: "#8BE9FD" }}>HEAD</span>
            </span>
            {isLoading && (
              <span style={{ color: "#64748B", fontSize: 11, marginLeft: 12 }}>
                <span className="dev-gitlog-loading-dot" />
                {t('blog.loading')}
              </span>
            )}
          </div>

          <div className="dev-gitlog-body">
            {/* Loading skeleton */}
            {isLoading && <GitlogSkeleton />}

            {/* Empty state — only shown after data has loaded */}
            {isEmpty && (
              <div className="dev-gitlog-empty">
                <span style={{ color: "#64748B" }}>{t('blog.empty_cmd')}</span>
                <br />
                <span style={{ color: "#475569", fontSize: 12 }}>{t('blog.empty_msg')}</span>
              </div>
            )}

            {/* Blog items */}
            {!isLoading && items.map((item: any, index: number) => {
              const hash = shortHash(item.documentId || item.id?.toString() || "");
              // Strapi blog fields: title, shortDesc, publishedAt, createdAt, image
              const title    = item.title || item.name || "Untitled";
              const desc     = item.shortDesc || item.description || "";
              const date     = formatDate(item.publishedAt || item.createdAt || item.date || "");
              const readTime = Math.max(1, Math.ceil(((item.shortDesc || "").length || 500) / 400));
              const tag      = item.category?.name || item.tag || "tin tức";

              return (
                <div
                  key={index}
                  className="dev-gitlog-item"
                  onClick={() => {
                    const target = item.documentId || item.id;
                    if (target) {
                      navigate(`/blog/${target}`);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }
                  }}
                >
                  {/* Timeline */}
                  <div className="dev-gitlog-timeline">
                    <div className="dev-gitlog-node" />
                    {index < items.length - 1 && <div className="dev-gitlog-line" />}
                  </div>

                  {/* Content */}
                  <div className="dev-gitlog-content">
                    <div className="dev-gitlog-meta">
                      <code className="dev-gitlog-hash">{hash}</code>
                      <span className="dev-gitlog-tag">{tag}</span>
                      {date && <span className="dev-gitlog-date">{date}</span>}
                      <span className="dev-gitlog-readtime">{readTime} min read</span>
                    </div>
                    <div className="dev-gitlog-title">{title}</div>
                    {desc && (
                      <div className="dev-gitlog-desc">
                        {desc.length > 130 ? desc.slice(0, 130) + "…" : desc}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
